import { ref, watch, onScopeDispose, getCurrentScope, type Ref } from 'vue'
import { onValue, ref as firebaseRef } from 'firebase/database'
import { useBugsnag } from '#imports'
import { useFirebase } from '~/shared/services/firebase'
import { useFirebaseAuth } from '~/shared/firebase/composables/useFirebaseAuth'
import type { FirebaseService } from '~/shared/services/firebase'

export function useAppPushNotificationStatus (
  firebasePath: Ref<string | null>,
  getFirebase: () => FirebaseService = useFirebase
): { statusMap: Ref<Record<string, number>>, isConnected: Ref<boolean>, disconnect: () => void } {
  const statusMap = ref<Record<string, number>>({})
  const isConnected = ref(false)

  let unsubscribe: (() => void) | null = null

  async function subscribe (path: string): Promise<void> {
    const { ensureAuthenticated } = useFirebaseAuth()
    await ensureAuthenticated()

    const { database, isInitialized } = getFirebase()

    if (!isInitialized || !database) {
      isConnected.value = false
      return
    }

    const dbRef = firebaseRef(database, path)

    unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const data = snapshot.val()
        if (data != null && typeof data === 'object') {
          const next: Record<string, number> = {}
          for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'number') {
              next[String(key)] = value
            }
          }
          statusMap.value = next
        } else {
          statusMap.value = {}
        }
        isConnected.value = true
      },
      (error) => {
        isConnected.value = false
        const bugsnag = useBugsnag()
        bugsnag?.notify(error, (event) => {
          event.addMetadata('firebase', { path, message: 'Push notification status listener error' })
        })
      }
    )
  }

  function disconnect (): void {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    statusMap.value = {}
    isConnected.value = false
  }

  const stopWatch = watch(
    firebasePath,
    (path) => {
      disconnect()
      if (path && path.trim().length > 0) {
        subscribe(path)
      }
    },
    { immediate: true }
  )

  const scope = getCurrentScope()
  if (scope) {
    onScopeDispose(() => {
      stopWatch()
      disconnect()
    })
  }

  return {
    statusMap,
    isConnected,
    disconnect
  }
}
